import React, { Component } from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { pick } from 'lodash';
import queryString from 'query-string';
import axios from 'axios';
import FormControlLabel from "@mui/material/FormControlLabel";

// Contexts
import { withTranslate, withTenant, withNavigation } from '../../core/TenantProvider';
import { getConfigProperty } from '../../core/EnvironmentConfig/EnvironmentConfig';
// Components - Atoms, Molecules, Organisms, Pages
import { APCheckbox } from "../../components/atoms/APInput";
import APButton from "../../components/atoms/APButton";
import APStepContainer from './../../components/organisms/APStepContainer';
import APLoadingSpinner from "./../../components/atoms/APLoadingSpinner";
// Files in the same folder
import InviteAffiliateContainerStyles from './styles/InviteAffiliateContainer.style';
import { SET_PASSWORD_FORM } from './definitions/setpassword';
// Redux
import { actions as servicesDowntimeAction } from '../../redux/modules/servicesDowntime';
// Utils
import { getOrganizationCode, getOrganizationName } from '../../core/utils/OrganizationValueGetter/OrganizationValueGetter';
import { getReferringCompany } from '../../core/utils/ReferringCompanyValueGetter/ReferringCompanyValueGetter';
import EncodeData from '../../core/utils/EncodeDecodeData';
import { CheckUSAOrCanadaAffiliate } from '../../core/utils/CheckUSAOrCanadaAffiliate/CheckUSAOrCanadaAffiliate';
import { getBasicAuthToken } from '../../core/utils/BasicAuthTokenGetter/BasicAuthTokenGetter';
// Constants
import {
    INVITE_AFFILIATE,
    CONFIRM_PASSWORD,
    PARTNER_PORTAL_APPROVAL,
    PARTNER_APPROVAL_SIGN_TS_AND_CS_TYPE
} from './../../core/utils/Constants/Constants';

interface InviteAffiliateContainerProps extends WithStyles<typeof InviteAffiliateContainerStyles> {
    translate: Function,
    getServicesErrorObj: Function
    location: any,
    navigate: any
}
interface InviteAffiliateContainerState {
    checkedTerms: boolean,
    isValid: boolean,
    toggleForm: boolean,
    processType: any,
    sfAccountId: any,
    sfContactID: string,
    loading: boolean,
    firstName: any,
    email: any
    country: any
}
class InviteAffiliateContainer extends Component<InviteAffiliateContainerProps, InviteAffiliateContainerState> {
    constructor(props: InviteAffiliateContainerProps) {
        super(props);
        this.state = {
            checkedTerms: false,
            isValid: false,
            toggleForm: false,
            processType: '',
            sfAccountId: '',
            sfContactID: '',
            loading: true,
            firstName: '',
            email: '',
            country: ''
        };
        this.onTermsCheckBoxChange = this.onTermsCheckBoxChange.bind(this);
        this.onContinue = this.onContinue.bind(this);
        this.onComplete = this.onComplete.bind(this);
        this.onHideSpinner = this.onHideSpinner.bind(this);
    }

    componentDidMount() {
        const queryParam: queryString.ParsedQuery<string> = queryString.parse(this.props.location.search);
        const {
            aid,
            cid,
            pt,
            fn,
            ctry
        } = queryParam;

        this.setState({ processType: pt, sfAccountId: aid, sfContactID: cid as string, firstName: fn, country: ctry });
    }

    onTermsCheckBoxChange(event: any) {
        const {
            target: {
                checked
            }
        } = event;

        this.setState({
            checkedTerms: checked
        }, () => {
            this.state.checkedTerms ? this.setState({ isValid: true }) : this.setState({ isValid: false });
        })
    }

    onContinue() {
        if (this.state.processType === PARTNER_APPROVAL_SIGN_TS_AND_CS_TYPE) {
            this.onComplete(null);
        } else {
            this.setState({
                toggleForm: true
            });
        }
    }

    approvePartnerEndPoint: string = getConfigProperty('apiEndPoint')['approvePartner'];

    getAxiosDefaults() {
        const referringCompany = getReferringCompany();
        const accessToken = getBasicAuthToken(referringCompany);

        return {
            "baseUrl": "",
            "headers": {
                "Access-Control-Allow-Origin": "*",
                "Authorization": `Basic ${accessToken}`,
                "Content-Type": "application/json"
            }
        };
    }

    onComplete(form: any) {
        const {
            getServicesErrorObj
        } = this.props;

        const {
            sfAccountId,
            sfContactID,
            processType,
            checkedTerms
        } = this.state;

        const referringCompany = getReferringCompany();
        let password = pick(form, [CONFIRM_PASSWORD]) ?? '',
            orgCode = getOrganizationCode(referringCompany),
            payload;

        const isSignTAndCOnly: boolean = this.state.processType === PARTNER_APPROVAL_SIGN_TS_AND_CS_TYPE;

        const InviteAffiliatePayload = {
            "type": "approvePartner",
            "organisation": orgCode,
            ...(!isSignTAndCOnly && { "password": password?.confirmPassword && EncodeData(password?.confirmPassword) }),
            "processType": processType,
            "SFAccount_ID": sfAccountId,
            ...(sfContactID && { "SFContact_ID": sfContactID })
        };

        const PartnerPortalApprovalPayload = {
            ...InviteAffiliatePayload,
            "tncAgreed": checkedTerms ? 'true' : 'false'
        };

        const PROCESS_TYPE_SELECTOR = {
            inviteAffiliate: InviteAffiliatePayload,
            partnerPortalApproval: PartnerPortalApprovalPayload,
            signTsAndCs: PartnerPortalApprovalPayload
        }
        payload = PROCESS_TYPE_SELECTOR[processType];

        const axiosDefaults = this.getAxiosDefaults();
        axios.defaults.baseURL = axiosDefaults.baseUrl;
        axios.defaults.headers.post = axiosDefaults.headers;
        axios
            .post(this.approvePartnerEndPoint, payload)
            .then((response: any) => {
                this.props.navigate('/login');
            })
            .catch((error) => {
                getServicesErrorObj(error)
            });
    }

    onHideSpinner() {
        this.setState({
            loading: false
        });
    }

    render() {
        const {
            classes,
            translate
        } = this.props;

        const {
            processType,
            toggleForm,
            loading,
            firstName,
            checkedTerms,
            isValid
        } = this.state;

        const referringCompany = getReferringCompany();
        const organizationName = getOrganizationName(referringCompany);

        let termsLinkURL: string = '';
        const isUSAOrCanadaAffiliate: boolean = CheckUSAOrCanadaAffiliate();
        if (isUSAOrCanadaAffiliate) {
            termsLinkURL = 'become.partner.summary.usa.terms.link.url';
        } else {
            termsLinkURL = 'become.partner.summary.terms.link.url';
        }

        const TermsAndConditionView = (
            <div className={classes.termsAndConditionsContainer}>
                <div className={classes.title}>
                    <h1>{translate('invite.affiliate.section.heading', {
                        FIRST_NAME: firstName
                    })}</h1>
                    <p>{translate('invite.affiliate.section.paragraph')}</p>
                </div>
                <div className={classes.section}>
                    <h2>{translate('invite.affiliate.terms.section.heading')}</h2>
                    <div className={classes.innerSection}>
                        <div className={classes.scrollableInner}>
                            {loading ? (
                                <APLoadingSpinner classeName={classes.loaderWrapper} />
                            ) : (null)}
                            <iframe
                                src={translate(termsLinkURL)}
                                height="100%"
                                width="100%"
                                onLoad={this.onHideSpinner}
                                frameBorder="0"
                                title="terms"
                            />
                        </div>
                    </div>
                    <div className={classes.checkboxWrapper}>
                        <FormControlLabel
                            value={checkedTerms}
                            control={<APCheckbox />}
                            name="termsCheckbox"
                            onChange={this.onTermsCheckBoxChange}
                            label={translate('invite.affiliate.terms.section.checkbox.label', {
                                BRAND_NAME: organizationName
                            })} />
                    </div>
                </div>
                <div className={classes.buttonWrapper}>
                    <APButton
                        children={translate('invite.affiliate.terms.section.button.label')}
                        className={classes.continueButton}
                        color="primary"
                        variant="contained"
                        onClick={this.onContinue}
                        disabled={!isValid}
                    />
                </div>
            </div>
        );

        const ChoosePasswordView = (
            <div className={classes.choosePasswordContainer}>
                <APStepContainer
                    onComplete={this.onComplete}
                    steps={SET_PASSWORD_FORM}
                    formName={INVITE_AFFILIATE}
                />
            </div>
        );

        let partnerApprovalView;
        if (processType === INVITE_AFFILIATE || toggleForm) {
            partnerApprovalView = ChoosePasswordView;
        } else if (processType === PARTNER_PORTAL_APPROVAL || processType === PARTNER_APPROVAL_SIGN_TS_AND_CS_TYPE) {
            partnerApprovalView = TermsAndConditionView;
        }

        return (
            <>
                {partnerApprovalView}
            </>
        )
    }
}

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
    getServicesErrorObj: servicesDowntimeAction.getServicesErrorObj
}, dispatch);

export default connect(null, mapDispatchToProps)(
    withNavigation(withTenant(
        withTranslate(
            withStyles(InviteAffiliateContainerStyles)(InviteAffiliateContainer)
        )
    )
    )
);